import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-hot-toast";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import { ActivityFilterBar } from "../components/ActivityFilterBar";
import { ActivitySearchBox } from "../components/ActivitySearchBox";
import { AuditTimeline } from "../components/AuditTimeline";
import { Button } from "../components/ui/Button";
import {
  ActivityFilterOptions,
  ActivityFilters,
  activityFiltersAreSet,
  activityFiltersFromParams,
  activityFiltersToParams,
} from "../lib/activity-filters";
import {
  ActivitySearchInterpreted,
  activityPageFromParams,
  activitySearchReading,
  applyActivitySearch,
} from "../lib/activity-search";
import {
  exportActivityCsv,
  fetchActivity,
  fetchActivityFilters,
  saveBlobDownload,
} from "../lib/audit-api";
import {
  ACTIVITY_PAGE_SIZE,
  AUDIT_SOURCE_NOTE,
  AuditEvent,
  pageCount,
} from "../lib/audit-history";
import { useAuth } from "../lib/useAuth";
import { useDatasources } from "../lib/useDatasources";

const EMPTY_OPTIONS: ActivityFilterOptions = { actors: [], tabs: [] };

const EXPORT_TRUNCATED_NOTE =
  "The export has the first 100,000 lines. Use filters to export less.";

/**
 * Activity: every change made to one panel, newest first (MOSA-368).
 *
 * The owner's view (Q20). A member gets 403 from the endpoint, and the page
 * says so rather than showing an empty list. Filters and CSV export are
 * MOSA-879 (slice 3). Search is MOSA-933. The API applies the filters and
 * the search text. This page does not parse the text.
 */

const apiMessage = (error: unknown): string | null => {
  const message = (error as { response?: { data?: { message?: unknown } } })
    ?.response?.data?.message;
  return typeof message === "string" && message.trim() ? message.trim() : null;
};
const Activity: React.FC = () => {
  const { datasourceId } = useParams<{ datasourceId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { token } = useAuth();
  const { datasources, isInitialLoad } = useDatasources();

  const datasource = datasources.find((ds) => ds.id === datasourceId);
  const panelName = datasource?.name || "this panel";

  const filters = useMemo(
    () => activityFiltersFromParams(searchParams),
    [searchParams]
  );
  const filtersKey = useMemo(
    () => activityFiltersToParams(filters).toString(),
    [filters]
  );
  const filtersSet = activityFiltersAreSet(filters);
  const page = activityPageFromParams(searchParams);

  const [events, setEvents] = useState<AuditEvent[]>([]);
  const [total, setTotal] = useState(0);
  const [interpreted, setInterpreted] =
    useState<ActivitySearchInterpreted | null>(null);
  const [filterOptions, setFilterOptions] =
    useState<ActivityFilterOptions>(EMPTY_OPTIONS);
  const [loadState, setLoadState] = useState<
    "loading" | "ready" | "forbidden" | "error"
  >("loading");
  const [searchError, setSearchError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [exportNote, setExportNote] = useState<string | null>(null);
  const [readingKey, setReadingKey] = useState(filtersKey);

  // Drop the previous reading before paint when the query changes, so it
  // does not sit under a new search. Page changes keep the same reading.
  if (readingKey !== filtersKey) {
    setReadingKey(filtersKey);
    setInterpreted(null);
    setSearchError(null);
  }

  const writeFilters = useCallback(
    (next: ActivityFilters) => {
      setSearchParams(activityFiltersToParams(next), { replace: true });
    },
    [setSearchParams]
  );

  const clearFilters = useCallback(() => {
    writeFilters({});
    setExportNote(null);
  }, [writeFilters]);

  const onSearch = useCallback(
    (q: string) => {
      setExportNote(null);
      setSearchParams(applyActivitySearch(searchParams, q), { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const goToPage = useCallback(
    (nextPage: number) => {
      const next = new URLSearchParams(searchParams);
      next.delete("tz");
      next.set("page", String(Math.max(1, nextPage)));
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  useEffect(() => {
    if (!datasourceId) return;
    let cancelled = false;
    setLoadState("loading");
    fetchActivity(datasourceId, page, { token, filters })
      .then((result) => {
        if (cancelled) return;
        setEvents(result.items);
        setTotal(result.total);
        setInterpreted(result.interpreted);
        setLoadState("ready");
      })
      .catch((error: { response?: { status?: number } }) => {
        if (cancelled) return;
        const status = error?.response?.status;
        setEvents([]);
        setTotal(0);
        setInterpreted(null);
        if (status === 403) {
          setLoadState("forbidden");
          return;
        }
        // A 400 body has `message`. Show that text. Do not add the search
        // text or a cell value.
        if (status === 400) setSearchError(apiMessage(error));
        setLoadState("error");
      });
    return () => {
      cancelled = true;
    };
  }, [datasourceId, page, token, filters]);

  useEffect(() => {
    if (!datasourceId) return;
    let cancelled = false;
    fetchActivityFilters(datasourceId, { token })
      .then((options) => {
        if (!cancelled) setFilterOptions(options);
      })
      .catch(() => {
        if (!cancelled) setFilterOptions(EMPTY_OPTIONS);
      });
    return () => {
      cancelled = true;
    };
  }, [datasourceId, token]);

  const onExport = useCallback(async () => {
    if (!datasourceId || exporting) return;
    setExporting(true);
    setExportNote(null);
    try {
      const result = await exportActivityCsv(datasourceId, filters, { token });
      saveBlobDownload(result.blob, result.filename);
      if (result.truncated) {
        setExportNote(EXPORT_TRUNCATED_NOTE);
      }
    } catch (error: unknown) {
      // Do not put cell values in the error text (A7 / task). A 400 from the
      // same search shows the API message and nothing else.
      const status = (error as { response?: { status?: number } })?.response
        ?.status;
      const message = status === 400 ? apiMessage(error) : null;
      toast.error(message || "Could not export the activity.");
    } finally {
      setExporting(false);
    }
  }, [datasourceId, exporting, filters, token]);

  const pages = pageCount(total, ACTIVITY_PAGE_SIZE);

  if (!datasource) {
    return (
      <div className="p-6 text-sm text-od-muted">
        {isInitialLoad ? "Loading…" : "That panel is not in your workspace."}
      </div>
    );
  }

  if (loadState === "forbidden") {
    return (
      <div className="max-w-2xl space-y-2 p-6">
        <h1 className="m-0 text-lg font-semibold text-od-text">Activity</h1>
        <p className="text-sm text-od-muted">
          Only the owner of {panelName} can see the activity of the whole panel.
          Open a record to read its own history.
        </p>
      </div>
    );
  }

  const searchSet = Boolean(filters.q);
  const reading = searchSet ? activitySearchReading(interpreted) : "";
  const emptySearch =
    loadState === "ready" && events.length === 0 && searchSet;
  const emptyFiltered =
    loadState === "ready" && events.length === 0 && !searchSet && filtersSet;
  const emptyPanel =
    loadState === "ready" && events.length === 0 && !filtersSet;

  return (
    <div className="mx-auto w-full max-w-3xl space-y-5 p-4 md:p-6">
      <header className="space-y-1.5">
        {/* A panel's landing route is a dashboard id this page does not know,
            so Back is history rather than a built path. Same as Share. */}
        <Button variant="link" size="sm" onClick={() => navigate(-1)}>
          <ArrowBackIcon sx={{ fontSize: 14 }} /> Back to {panelName}
        </Button>
        <h1 className="m-0 text-lg font-semibold text-od-text">
          Activity in {panelName}
        </h1>
        <p className="text-sm text-od-muted">
          {AUDIT_SOURCE_NOTE} Each entry names the person, the time and the
          change. An edit made in Google Sheets does not show here.
        </p>
      </header>

      <div className="space-y-3">
        <ActivitySearchBox
          q={filters.q ?? ""}
          reading={reading}
          error={searchError}
          onSearch={onSearch}
        />

        <ActivityFilterBar
          filters={filters}
          options={filterOptions}
          exporting={exporting}
          onChange={(next) => {
            setExportNote(null);
            writeFilters(next);
          }}
          onClear={clearFilters}
          onExport={onExport}
        />
      </div>

      {exportNote && (
        <p className="text-sm text-od-warning" role="status">
          {exportNote}
        </p>
      )}

      {loadState === "loading" && (
        <p className="text-sm text-od-muted">Loading the activity…</p>
      )}

      {loadState === "error" && !searchError && (
        <p className="text-sm text-od-warning">
          Could not load the activity. Reload the page to try again.
        </p>
      )}

      {emptySearch && (
        <div className="space-y-2">
          <p className="text-sm text-od-muted" role="status">
            No changes match this search.
          </p>
          <Button variant="ghost" size="sm" onClick={clearFilters}>
            Clear filters
          </Button>
        </div>
      )}

      {emptyPanel && (
        <p className="text-sm text-od-muted">
          No changes yet. An edit, an add, a delete or an import through
          OnlyDash shows here.
        </p>
      )}

      {emptyFiltered && (
        <div className="space-y-2">
          <p className="text-sm text-od-muted">
            No changes match these filters.
          </p>
          <Button variant="ghost" size="sm" onClick={clearFilters}>
            Clear filters
          </Button>
        </div>
      )}

      {loadState === "ready" && events.length > 0 && (
        <>
          <AuditTimeline events={events} />
          {pages > 1 && (
            <nav
              aria-label="Activity pages"
              className="flex items-center justify-between gap-3"
            >
              <Button
                variant="secondary"
                size="sm"
                disabled={page <= 1}
                onClick={() => goToPage(page - 1)}
              >
                Newer
              </Button>
              <span className="text-xs text-od-muted">
                Page {page} of {pages}
              </span>
              <Button
                variant="secondary"
                size="sm"
                disabled={page >= pages}
                onClick={() => goToPage(page + 1)}
              >
                Older
              </Button>
            </nav>
          )}
        </>
      )}
    </div>
  );
};

export default Activity;
